import { POST_LENGTHS } from "./constants/postSettings";

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ref, onChildAdded, onChildChanged, onChildRemoved, remove, get } from "firebase/database";
import { db } from "./firebase/config";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { getTotalActivePostingHours } from "./AutoPostGenerator/utils/autoPostLimitUtils";

const AutoPostConfigScreen = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "active", "inactive"
  const [selectedFanpage, setSelectedFanpage] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isFanpageDropdownOpen, setIsFanpageDropdownOpen] = useState(false);
  const [hourLimitInfo, setHourLimitInfo] = useState({
    totalHours: 0,
    maxHours: 5,
    remaining: 5,
  });
  const statusDropdownRef = useRef(null);
  const fanpageDropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const configsRef = ref(db, `/posts/${userId}/`);
    
    const configMap = new Map();
    let isInitialLoad = true;

    // OPTIMIZED: Use child listeners instead of onValue to avoid downloading entire node on each change
    // This reduces bandwidth: only changed config is downloaded, not all configs
    const unsubscribeAdded = onChildAdded(configsRef, (snapshot) => {
      const id = snapshot.key;
      configMap.set(id, { id, ...snapshot.val() });
      
      if (isInitialLoad) {
        setLoading(false);
        isInitialLoad = false;
      }
      updateConfigsState();
    });

    const unsubscribeChanged = onChildChanged(configsRef, (snapshot) => {
      const id = snapshot.key;
      configMap.set(id, { id, ...snapshot.val() });
      updateConfigsState();
    });

    const unsubscribeRemoved = onChildRemoved(configsRef, (snapshot) => {
      const id = snapshot.key;
      configMap.delete(id);
      updateConfigsState();
    });

    const updateConfigsState = () => {
      const configList = Array.from(configMap.values());
      // Sort the configList by posting time in ascending order
      configList.sort((a, b) => {
        const timeA = a.time
          ? new Date(`1970-01-01T${a.time}:00Z`)
          : new Date();
        const timeB = b.time
          ? new Date(`1970-01-01T${b.time}:00Z`)
          : new Date();
        return timeA - timeB;
      });
      setConfigs(configList);
    };

    // Load hour limit info
    const loadHourLimit = async () => {
      const info = await getTotalActivePostingHours(userId);
      setHourLimitInfo(info);
    };
    loadHourLimit();

    return () => {
      unsubscribeAdded();
      unsubscribeChanged();
      unsubscribeRemoved();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
      if (
        fanpageDropdownRef.current &&
        !fanpageDropdownRef.current.contains(event.target)
      ) {
        setIsFanpageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const deleteConfig = async (configId) => {
    confirmAlert({
      title: "Confirm to delete",
      message: "Are you sure you want to delete this configuration?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const userId = localStorage.getItem("userId");
              const configRef = ref(db, `/posts/${userId}/${configId}`);
              await remove(configRef);
            } catch (error) {
              console.error("Error deleting config:", error);
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const getPostLengthLabel = (postLength) => {
    const foundLength = POST_LENGTHS.find(
      (length) => length.value === postLength
    );
    return foundLength ? foundLength.label : "Not set";
  };

  const openFanpage = (fanpageID) => {
    window.open(
      `https://www.facebook.com/profile.php?id=${fanpageID}`,
      "_blank"
    );
  };

  const getFanpageOrWebsiteOptions = () => {
    const uniqueOptions = [
      ...new Set(
        configs.flatMap((config) => [
          config.fanpageName,
          config.wordpressSiteName || config.websiteUrl,
        ])
      ),
    ];
    return uniqueOptions.filter(Boolean); // Lọc bỏ các giá trị null/undefined
  };

  const filteredConfigs = configs.filter((config) => {
    const matchesSearch =
      config.prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.fanpageName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "active"
        ? config.autoPostEnabled
        : filterStatus === "inactive"
        ? !config.autoPostEnabled
        : true;

    const matchesFanpage =
      selectedFanpage === "all"
        ? true
        : config.fanpageName === selectedFanpage ||
          config.wordpressSiteName === selectedFanpage ||
          config.websiteUrl === selectedFanpage;

    return matchesSearch && matchesStatus && matchesFanpage;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          THIẾT LẬP VIẾT & ĐĂNG BÀI TỰ ĐỘNG ĐÃ LƯU
        </h1>

        {/* Hiển thị thông tin giới hạn giờ đăng bài */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">
                Giới hạn giờ đăng bài tự động
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                Tổng số giờ đăng bài của các thiết lập đang hoạt động
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-800">
                {hourLimitInfo.totalHours}/{hourLimitInfo.maxHours}
              </p>
              <p className="text-sm text-blue-600">giờ đã dùng</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                hourLimitInfo.totalHours >= hourLimitInfo.maxHours
                  ? 'bg-red-500'
                  : hourLimitInfo.totalHours >= 4
                  ? 'bg-yellow-500'
                  : 'bg-blue-500'
              }`}
              style={{
                width: `${(hourLimitInfo.totalHours / hourLimitInfo.maxHours) * 100}%`
              }}
            />
          </div>
        </div>

        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm hoặc tên fanpage..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          <div className="md:w-40 relative" ref={fanpageDropdownRef}>
            <button
              onClick={() => setIsFanpageDropdownOpen(!isFanpageDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedFanpage === "all" ? "Chọn trang" : selectedFanpage}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isFanpageDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isFanpageDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setSelectedFanpage("all");
                    setIsFanpageDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Chọn trang
                </button>
                {getFanpageOrWebsiteOptions().map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedFanpage(option);
                      setIsFanpageDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                    title={option}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredConfigs.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {configs.length === 0
              ? "Không có cấu hình nào được tạo. Bạn có thể tạo cấu hình mới"
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredConfigs.map((item) => (
              <div
                key={item.id}
                onClick={() =>
                  navigate("/auto-post", { state: { config: item } })
                }
                className="bg-white rounded-xl shadow-lg overflow-hidden transform transition duration-300 hover:scale-105 cursor-pointer"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-xl text-gray-800 line-clamp-2">
                      {item.prompt || "Thiết lập tự động"}
                    </h3>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-gray-600">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span>
                          {Array.isArray(item.times) && item.times.length > 0
                            ? `${item.times.length} giờ`
                            : item.time
                            ? "1 giờ"
                            : "Chưa đặt giờ"}
                        </span>
                      </div>
                      <div
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          item.autoPostEnabled
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.autoPostEnabled ? "🔵 Đang chạy" : "⚪ Đã tắt"}
                      </div>
                    </div>

                    <div className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M4 6h16M4 12h16M4 18h7"></path>
                      </svg>
                      <span>{getPostLengthLabel(item.postLength)}</span>
                    </div>

                    <div className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                      <span>{item.postStyles || "Chưa chọn phong cách"}</span>
                    </div>

                    {item.platform === "wordpress" ? (
                      <div className="flex items-center text-gray-600">
                        {/* WordPress/Website Icon */}
                        <svg
                          className="w-5 h-5 mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          ></path>
                        </svg>
                        <span
                          className="text-blue-500 hover:text-blue-700 truncate cursor-pointer" // Thêm truncate và cursor-pointer
                          onClick={(e) => {
                            e.stopPropagation();
                            // Mở link website nếu có
                            if (item.websiteUrl) {
                              // Đảm bảo URL có http/https
                              let url = item.websiteUrl;
                              if (!url.match(/^https?:\/\//i)) {
                                url = "https://" + url;
                              }
                              window.open(url, "_blank");
                            }
                          }}
                          title={
                            item.wordpressSiteName || // Thay đổi ở đây
                            item.websiteUrl ||
                            "Chưa có website"
                          } // Hiển thị tooltip
                        >
                          {item.wordpressSiteName || // Thay đổi ở đây
                            item.websiteUrl ||
                            "Chưa có website"}
                        </span>
                      </div>
                    ) : (
                      // Code cũ cho Facebook
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2 flex-shrink-0" // Thêm flex-shrink-0
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                        <span
                          className="text-blue-500 hover:text-blue-700 truncate cursor-pointer" // Thêm truncate và cursor-pointer
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent navigation when clicking fanpage link
                            openFanpage(item.fanpageID);
                          }}
                          title={item.fanpageName || "Chưa chọn fanpage"} // Hiển thị tooltip
                        >
                          {item.fanpageName || "Chưa chọn fanpage"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoPostConfigScreen;
